// Peças visuais da calculadora de avaliação (AVM) — usadas pelos passos e pelo resultado.
// Sem `window`/`document` no render (o bloco é renderizado no SSR junto com a página).
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { AvmOpcao } from './avm.utils';
import styles from './AvmCalculator.module.css';

/* ── ícones (SVG inline: o site não carrega bootstrap-icons) ────────────────── */

type NomeIcone =
    | 'apartamento'
    | 'casa'
    | 'terreno'
    | 'rural'
    | 'galpao'
    | 'comercial'
    | 'imovel'
    | 'check'
    | 'alerta'
    | 'whatsapp'
    | 'busca'
    | 'seta';

const TRACOS: Record<NomeIcone, string> = {
    apartamento: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 21V10h4a1 1 0 0 1 1 1v10M4 21h16M7 8h3M7 12h3M7 16h3',
    casa: 'M3 10.5 12 3l9 7.5M5.5 9.5V21h13V9.5M10 21v-6h4v6',
    terreno: 'M3 19h18M5 19V9l7-4 7 4v10M9 19v-4h6v4',
    rural: 'M3 20h18M6 20v-7l6-4 6 4v7M10 20v-4h4v4M4 12c1.6-2.4 3.2-3.2 5-2.4',
    galpao: 'M3 20V10l9-5 9 5v10M3 20h18M8 20v-6h8v6M8 14h8',
    comercial: 'M4 21V6l8-3 8 3v15M4 21h16M9 21v-5h6v5M8 9h2M14 9h2M8 13h2M14 13h2',
    imovel: 'M4 21V8l8-5 8 5v13M4 21h16M9 21v-6h6v6',
    check: 'M4.5 12.5 9.5 17.5 19.5 6.5',
    alerta: 'M12 8v5M12 17h.01M10.3 3.9 2.6 17.4a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
    whatsapp:
        'M3.5 20.5 4.9 16a8 8 0 1 1 3.1 3.1l-4.5 1.4ZM9 9.5c0 3 2.5 5.5 5.5 5.5.6 0 1.2-.6 1.2-1.2l-1.6-.8-1 1a6 6 0 0 1-2.6-2.6l1-1-.8-1.6c-.6 0-1.7.2-1.7 1.2Z',
    busca: 'M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16ZM21 21l-4.3-4.3',
    seta: 'M6 9l6 6 6-6',
};

export const Icone = ({
    nome,
    tamanho = 24,
    trecho = 1.6,
}: {
    nome: NomeIcone;
    tamanho?: number;
    trecho?: number;
}) => (
    <svg
        width={tamanho}
        height={tamanho}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={trecho}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        <path d={TRACOS[nome]} />
    </svg>
);

/* ── campo (rótulo + erro + ajuda) ─────────────────────────────────────────── */

export const Campo = ({
    id,
    rotulo,
    opcional,
    erro,
    ajuda,
    children,
}: {
    id: string;
    rotulo: string;
    opcional?: boolean;
    erro?: string;
    ajuda?: string;
    children: React.ReactNode;
}) => (
    <div className={styles.campo}>
        <label className={styles.rotulo} htmlFor={id}>
            {rotulo}
            {opcional ? <span className={styles.opcional}>(opcional)</span> : null}
        </label>
        {children}
        {erro ? (
            <span className={styles.erro} id={`${id}-erro`} role="alert">
                <Icone nome="alerta" tamanho={14} trecho={2} />
                {erro}
            </span>
        ) : ajuda ? (
            <span className={styles.ajuda}>{ajuda}</span>
        ) : null}
    </div>
);

/* ── combo pesquisável (cidade / bairro) ───────────────────────────────────── */

const normaliza = (v: string) =>
    String(v || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

export interface AvmComboProps {
    id: string;
    texto: string;
    valorId: string;
    opcoes: AvmOpcao[];
    placeholder?: string;
    disabled?: boolean;
    invalido?: boolean;
    /** bairro sem lista: o texto digitado vale por si só. */
    permitirLivre?: boolean;
    aoMudar: (_opcao: AvmOpcao | null, _texto: string) => void;
    aoSair?: () => void;
}

/**
 * Combobox simples: input + lista filtrada (sem dependência nova). Preferido ao
 * <select> nativo porque um site com 80 cidades vira um rolê de 80 linhas no celular.
 */
export const AvmCombo = ({
    id,
    texto,
    valorId,
    opcoes,
    placeholder,
    disabled,
    invalido,
    permitirLivre,
    aoMudar,
    aoSair,
}: AvmComboProps) => {
    const [aberto, setAberto] = useState(false);
    const [ativo, setAtivo] = useState(0);
    const fecharRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const listaId = `${id}-lista`;

    const filtradas = useMemo(() => {
        const alvo = normaliza(texto);
        // com opção já escolhida, o texto é o nome dela: mostra a lista inteira
        const base = !alvo || valorId ? opcoes : opcoes.filter((o) => normaliza(o.nome).indexOf(alvo) >= 0);
        return base.slice(0, 60);
    }, [opcoes, texto, valorId]);

    useEffect(
        () => () => {
            if (fecharRef.current) clearTimeout(fecharRef.current);
        },
        [],
    );

    const escolher = (opcao: AvmOpcao) => {
        aoMudar(opcao, opcao.nome);
        setAberto(false);
    };

    const aoTeclar = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!filtradas.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!aberto) {
                setAberto(true);
                setAtivo(0);
                return;
            }
            setAtivo((i) => {
                const proximo = e.key === 'ArrowDown' ? i + 1 : i - 1;
                return (proximo + filtradas.length) % filtradas.length;
            });
            return;
        }
        if (e.key === 'Enter' && aberto) {
            // Enter com a lista aberta escolhe a opção — não avança o passo.
            e.preventDefault();
            e.stopPropagation();
            const opcao = filtradas[ativo] || filtradas[0];
            if (opcao) escolher(opcao);
            return;
        }
        if (e.key === 'Escape' && aberto) {
            e.stopPropagation();
            setAberto(false);
        }
    };

    const aoPerderFoco = () => {
        // atraso: o clique numa opção acontece depois do blur do input
        fecharRef.current = setTimeout(() => {
            setAberto(false);
            if (!valorId && !permitirLivre && texto.trim()) {
                const exata = opcoes.find((o) => normaliza(o.nome) === normaliza(texto));
                const unica = exata || (filtradas.length === 1 ? filtradas[0] : null);
                if (unica) aoMudar(unica, unica.nome);
            }
            if (aoSair) aoSair();
        }, 140);
    };

    return (
        <div className={styles.combo}>
            <input
                id={id}
                name={id}
                type="text"
                className={`${styles.input} ${invalido ? styles.inputErro : ''}`}
                value={texto}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete="off"
                role="combobox"
                aria-expanded={aberto}
                aria-controls={listaId}
                aria-autocomplete="list"
                aria-invalid={invalido ? true : undefined}
                aria-describedby={invalido ? `${id}-erro` : undefined}
                onChange={(e) => {
                    aoMudar(null, e.target.value);
                    setAtivo(0);
                    setAberto(true);
                }}
                onFocus={() => setAberto(true)}
                onClick={() => setAberto(true)}
                onKeyDown={aoTeclar}
                onBlur={aoPerderFoco}
            />
            {aberto && !disabled ? (
                <ul className={styles.comboLista} id={listaId} role="listbox">
                    {filtradas.length ? (
                        filtradas.map((opcao, i) => (
                            <li
                                key={opcao.id}
                                role="option"
                                aria-selected={String(opcao.id) === valorId}
                                className={`${styles.comboOpcao} ${i === ativo ? styles.comboOpcaoAtiva : ''}`}
                                onMouseEnter={() => setAtivo(i)}
                                onMouseDown={(e) => {
                                    // mousedown (não click): roda antes do blur do input
                                    e.preventDefault();
                                    escolher(opcao);
                                }}
                            >
                                {opcao.nome}
                            </li>
                        ))
                    ) : (
                        <li className={styles.comboVazio}>
                            {permitirLivre ? 'Pode escrever o nome do bairro.' : 'Nenhuma opção encontrada.'}
                        </li>
                    )}
                </ul>
            ) : null}
        </div>
    );
};

/* ── stepper (quartos, suítes, banheiros, vagas) ───────────────────────────── */

export const Stepper = ({
    rotulo,
    valor,
    aoMudar,
    max = 20,
    disabled,
}: {
    rotulo: string;
    valor: number;
    aoMudar: (_v: number) => void;
    max?: number;
    disabled?: boolean;
}) => {
    const rotuloId = `avm-stepper-${normaliza(rotulo).replace(/\s+/g, '-')}`;
    return (
        <div className={styles.stepper}>
            <span className={styles.stepperRotulo} id={rotuloId}>
                {rotulo}
            </span>
            <div className={styles.stepperControles}>
                <button
                    type="button"
                    className={styles.stepperBotao}
                    onClick={() => aoMudar(Math.max(0, valor - 1))}
                    disabled={disabled || valor <= 0}
                    aria-label={`Diminuir ${rotulo}`}
                >
                    −
                </button>
                <output className={styles.stepperValor} aria-labelledby={rotuloId}>
                    {valor}
                </output>
                <button
                    type="button"
                    className={styles.stepperBotao}
                    onClick={() => aoMudar(Math.min(max, valor + 1))}
                    disabled={disabled || valor >= max}
                    aria-label={`Aumentar ${rotulo}`}
                >
                    +
                </button>
            </div>
        </div>
    );
};

/* ── toggle (tem elevador?) ────────────────────────────────────────────────── */

export const Toggle = ({
    rotulo,
    ativo,
    aoMudar,
    disabled,
}: {
    rotulo: string;
    ativo: boolean;
    aoMudar: (_v: boolean) => void;
    disabled?: boolean;
}) => (
    <button
        type="button"
        className={styles.toggle}
        role="switch"
        aria-checked={ativo}
        disabled={disabled}
        onClick={() => aoMudar(!ativo)}
    >
        <span>{rotulo}</span>
        <span className={`${styles.toggleTrilho} ${ativo ? styles.toggleTrilhoAtivo : ''}`}>
            <span className={styles.toggleBolinha} />
        </span>
    </button>
);

/* ── chip (diferenciais) ───────────────────────────────────────────────────── */

export const Chip = ({
    rotulo,
    ativo,
    aoMudar,
    disabled,
}: {
    rotulo: string;
    ativo: boolean;
    aoMudar: (_v: boolean) => void;
    disabled?: boolean;
}) => (
    <button
        type="button"
        className={`${styles.chip} ${ativo ? styles.chipAtivo : ''}`}
        role="checkbox"
        aria-checked={ativo}
        disabled={disabled}
        onClick={() => aoMudar(!ativo)}
    >
        {ativo ? <Icone nome="check" tamanho={14} trecho={2.6} /> : null}
        {rotulo}
    </button>
);

/** id estável entre servidor e cliente (React 18) para casar label/input. */
export const useIdEstavel = (prefixo: string) => {
    const bruto = useId();
    return `${prefixo}-${bruto.replace(/:/g, '')}`;
};
